import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { inventoryService } from '../services/inventoryService';
import { orderService } from '../services/orderService';
import './ExpiryCalendar.css';

interface ExpiryEntry {
  expiryDate: string;
  itemId: string;
  itemName: string;
  categoryId: string;
  compartmentName: string;
  modulePackName?: string;
  materialName: string;
  count: number;
  daysLeft: number;
  orderLocation: string;
  instanceIdentifier?: string;
  orderedOn?: string;
}

const normalize = (value?: string) => (value || '').trim().toLowerCase();

const buildEntryOrderKey = (entry: Pick<ExpiryEntry, 'itemId' | 'materialName' | 'compartmentName' | 'modulePackName' | 'orderLocation'>) => {
  const compartment = entry.modulePackName
    ? `${entry.compartmentName} - ${entry.modulePackName}`
    : entry.compartmentName;
  return [
    normalize(entry.itemId),
    normalize(entry.materialName),
    normalize(compartment),
    normalize(entry.orderLocation),
  ].join('|');
};

const formatOrderDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const getDaysLeft = (dateString: string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(`${dateString}T00:00:00`);
  const diff = expiry.getTime() - today.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const formatDate = (dateString: string) => {
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString('de-DE', {
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const getMonthKey = (dateString: string) => dateString.slice(0, 7);

const addMonths = (monthKey: string, offset: number) => {
  const [year, month] = monthKey.split('-').map(Number);
  const date = new Date(year, month - 1 + offset, 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const monthDiff = (from: string, to: string) => {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm);
};

const formatMonthLabel = (monthKey: string) => {
  const [year, month] = monthKey.split('-').map(Number);
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
};

export default function ExpiryCalendar() {
  const [entries, setEntries] = useState<ExpiryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [orderingKey, setOrderingKey] = useState<string>('');

  useEffect(() => {
    loadExpiries();
  }, []);

  const loadExpiries = async () => {
    setLoading(true);
    setError('');
    try {
      const [items, orders] = await Promise.all([
        inventoryService.getItems(),
        orderService.getOrders().catch(() => []),
      ]);
      const itemDetails = await Promise.all(
        items.map((item) => inventoryService.getItem(item.id).catch(() => null))
      );

      const orderedMap = new Map<string, string>();
      orders
        .filter((order) => order.status !== 'cancelled')
        .forEach((order) => {
          const key = [
            normalize(order.itemReference.itemId || ''),
            normalize(order.itemReference.name),
            normalize(order.itemReference.compartment || ''),
            normalize(order.location || ''),
          ].join('|');

          const existing = orderedMap.get(key);
          if (!existing || new Date(order.requestDate) > new Date(existing)) {
            orderedMap.set(key, order.requestDate);
          }
        });

      const collected: ExpiryEntry[] = [];

      itemDetails.forEach((item) => {
        if (!item?.compartments) return;

        const itemName = item.data.bezeichnung || item.data.id;
        const orderLocation = item.data.bezeichnung || item.data.id;
        const defaultInstanceIdentifier = item.data.instances?.[0]?.identifier;

        item.compartments.forEach((comp) => {
          (comp.contents.items || []).forEach((compItem: any) => {
            (compItem.instances || []).forEach((instance: any) => {
              if (!instance?.expiryDate) return;
              collected.push({
                expiryDate: instance.expiryDate,
                itemId: item.id,
                itemName,
                categoryId: item.categoryId,
                compartmentName: comp.name,
                materialName: compItem.name,
                count: 1,
                daysLeft: getDaysLeft(instance.expiryDate),
                orderLocation,
                instanceIdentifier: defaultInstanceIdentifier,
              });
            });
          });

          (comp.contents.modulePacks || []).forEach((modulePack: any) => {
            (modulePack.items || []).forEach((mpItem: any) => {
              (mpItem.instances || []).forEach((instance: any) => {
                if (!instance?.expiryDate) return;
                collected.push({
                  expiryDate: instance.expiryDate,
                  itemId: item.id,
                  itemName,
                  categoryId: item.categoryId,
                  compartmentName: comp.name,
                  modulePackName: modulePack.name,
                  materialName: mpItem.name,
                  count: 1,
                  daysLeft: getDaysLeft(instance.expiryDate),
                  orderLocation,
                  instanceIdentifier: defaultInstanceIdentifier,
                });
              });
            });
          });
        });
      });

      const aggregatedMap = new Map<string, ExpiryEntry>();
      collected.forEach((entry) => {
        const key = [
          entry.expiryDate,
          entry.itemId,
          entry.categoryId,
          entry.compartmentName,
          entry.modulePackName || '',
          entry.materialName,
        ].join('|');

        const existing = aggregatedMap.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          aggregatedMap.set(key, {
            ...entry,
            orderedOn: orderedMap.get(buildEntryOrderKey(entry)),
          });
        }
      });

      const aggregated = Array.from(aggregatedMap.values());

      aggregated.sort((a, b) => {
        if (a.expiryDate !== b.expiryDate) return a.expiryDate.localeCompare(b.expiryDate);
        if (a.itemName !== b.itemName) return a.itemName.localeCompare(b.itemName);
        return a.materialName.localeCompare(b.materialName);
      });

      setEntries(aggregated);
    } catch (err) {
      setError('Fehler beim Laden der Ablaufdaten');
    } finally {
      setLoading(false);
    }
  };

  const monthOptions = useMemo(() => {
    return Array.from(new Set(entries.map((entry) => entry.expiryDate.slice(0, 7)))).sort();
  }, [entries]);

  const filteredEntries = useMemo(() => {
    if (!monthFilter) return entries;
    return entries.filter((entry) => entry.expiryDate.startsWith(monthFilter));
  }, [entries, monthFilter]);

  const groupedByDate = useMemo(() => {
    return filteredEntries.reduce<Record<string, ExpiryEntry[]>>((acc, entry) => {
      if (!acc[entry.expiryDate]) acc[entry.expiryDate] = [];
      acc[entry.expiryDate].push(entry);
      return acc;
    }, {});
  }, [filteredEntries]);

  const dateKeys = useMemo(() => Object.keys(groupedByDate).sort(), [groupedByDate]);

  const expiredCount = filteredEntries
    .filter((entry) => entry.daysLeft < 0)
    .reduce((sum, entry) => sum + entry.count, 0);
  const next30Count = filteredEntries
    .filter((entry) => entry.daysLeft >= 0 && entry.daysLeft <= 30)
    .reduce((sum, entry) => sum + entry.count, 0);
  const totalCount = filteredEntries.reduce((sum, entry) => sum + entry.count, 0);

  const monthlySeries = useMemo(() => {
    if (filteredEntries.length === 0) return [] as Array<{ month: string; count: number }>;

    const byMonth = new Map<string, number>();
    filteredEntries.forEach((entry) => {
      const key = getMonthKey(entry.expiryDate);
      byMonth.set(key, (byMonth.get(key) || 0) + entry.count);
    });

    const sortedMonths = Array.from(byMonth.keys()).sort();
    const first = sortedMonths[0];
    const last = sortedMonths[sortedMonths.length - 1];
    const totalMonths = monthDiff(first, last) + 1;

    const filled: Array<{ month: string; count: number }> = [];
    for (let i = 0; i < totalMonths; i += 1) {
      const monthKey = addMonths(first, i);
      filled.push({ month: monthKey, count: byMonth.get(monthKey) || 0 });
    }
    return filled;
  }, [filteredEntries]);

  const chartMax = useMemo(() => {
    const maxValue = monthlySeries.reduce((max, point) => Math.max(max, point.count), 0);
    return Math.max(1, maxValue);
  }, [monthlySeries]);

  const handleQuickOrder = async (entry: ExpiryEntry) => {
    const orderKey = [
      entry.expiryDate,
      entry.itemId,
      entry.materialName,
      entry.compartmentName,
      entry.modulePackName || '',
    ].join('|');

    try {
      setOrderingKey(orderKey);
      await orderService.createOrder({
        itemReference: {
          itemId: entry.itemId,
          name: entry.materialName,
          compartment: entry.modulePackName
            ? `${entry.compartmentName} - ${entry.modulePackName}`
            : entry.compartmentName,
        },
        quantity: entry.count,
        location: entry.orderLocation,
        notes: `Aus Verfallskalendar (${entry.expiryDate})`,
        instanceIdentifier: entry.instanceIdentifier,
      });

      setEntries((prev) => prev.map((existingEntry) => {
        if (buildEntryOrderKey(existingEntry) !== buildEntryOrderKey(entry)) return existingEntry;
        return {
          ...existingEntry,
          orderedOn: new Date().toISOString(),
        };
      }));

      alert(`✅ Bestellung erstellt: ${entry.materialName} (x${entry.count})`);
    } catch (err) {
      alert('Fehler beim Erstellen der Bestellung');
    } finally {
      setOrderingKey('');
    }
  };

  return (
    <div className="expiry-calendar-page">
      <div className="page-header">
        <h1>Verfallskalendar</h1>
        <p>Alle erfassten Ablaufdaten mit direktem Link zum Lagerort im Inventar.</p>
      </div>

      <div className="expiry-toolbar">
        <div className="filter-group">
          <label>Monat</label>
          <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
            <option value="">Alle</option>
            {monthOptions.map((month) => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
        </div>
        <button className="expiry-refresh-btn" onClick={loadExpiries}>Aktualisieren</button>
      </div>

      {monthlySeries.length > 0 && (
        <div className="expiry-chart-card">
          <h3>Verfälle pro Monat</h3>
          <svg className="expiry-chart-svg" viewBox="0 0 960 260" role="img" aria-label="Monatliche Anzahl ablaufender Items">
            {(() => {
              const width = 960;
              const height = 260;
              const margin = { top: 16, right: 12, bottom: 42, left: 52 };
              const innerW = width - margin.left - margin.right;
              const innerH = height - margin.top - margin.bottom;
              const slot = innerW / monthlySeries.length;
              const barWidth = Math.max(4, slot * 0.72);
              const labelStep = Math.max(1, Math.ceil(monthlySeries.length / 6));

              const yTicks = Array.from(new Set([0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(chartMax * ratio)))).sort((a, b) => a - b);

              return (
                <>
                  <line x1={margin.left} y1={margin.top} x2={margin.left} y2={margin.top + innerH} className="axis-line" />
                  <line x1={margin.left} y1={margin.top + innerH} x2={margin.left + innerW} y2={margin.top + innerH} className="axis-line" />

                  {yTicks.map((tick) => {
                    const y = margin.top + innerH - (tick / chartMax) * innerH;
                    return (
                      <g key={`y-${tick}`}>
                        <line x1={margin.left} y1={y} x2={margin.left + innerW} y2={y} className="grid-line" />
                        <text x={margin.left - 8} y={y + 4} textAnchor="end" className="axis-label">{tick}</text>
                      </g>
                    );
                  })}

                  {monthlySeries.map((point, i) => {
                    const valueHeight = (point.count / chartMax) * innerH;
                    const x = margin.left + i * slot + (slot - barWidth) / 2;
                    const y = margin.top + innerH - valueHeight;
                    const showLabel = i % labelStep === 0 || i === monthlySeries.length - 1;
                    return (
                      <g key={point.month}>
                        <rect x={x} y={y} width={barWidth} height={valueHeight} className="bar-rect" />
                        {showLabel && (
                          <text
                            x={margin.left + i * slot + slot / 2}
                            y={margin.top + innerH + 18}
                            textAnchor="middle"
                            className="axis-label"
                          >
                            {formatMonthLabel(point.month)}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </>
              );
            })()}
          </svg>
        </div>
      )}

      <div className="expiry-stats">
        <div className="stat-card">
          <span className="stat-label">Einträge</span>
          <strong>{totalCount}</strong>
        </div>
        <div className="stat-card danger">
          <span className="stat-label">Abgelaufen</span>
          <strong>{expiredCount}</strong>
        </div>
        <div className="stat-card warning">
          <span className="stat-label">Nächste 30 Tage</span>
          <strong>{next30Count}</strong>
        </div>
      </div>

      {loading && <div className="loading">Lädt...</div>}
      {error && <div className="error-message">{error}</div>}

      {!loading && !error && dateKeys.length === 0 && (
        <div className="empty-state">Keine Ablaufdaten vorhanden.</div>
      )}

      {!loading && !error && dateKeys.length > 0 && (
        <div className="calendar-list">
          {dateKeys.map((dateKey) => (
            <section key={dateKey} className="calendar-day-group">
              <h3>
                {formatDate(dateKey)}{' '}
                <span>({groupedByDate[dateKey].reduce((sum, entry) => sum + entry.count, 0)})</span>
              </h3>
              <div className="day-entries">
                {groupedByDate[dateKey].map((entry, index) => (
                  <div
                    key={`${dateKey}-${entry.itemId}-${entry.materialName}-${entry.compartmentName}-${entry.modulePackName || ''}-${index}`}
                    className="expiry-entry"
                  >
                    <Link
                      to={`/inventory/${entry.itemId}?category=${entry.categoryId}`}
                      className="entry-main"
                    >
                      <strong>
                        {entry.materialName}
                        {entry.count > 1 && <span className="entry-count">x{entry.count}</span>}
                      </strong>
                      <span>{entry.itemName} → {entry.compartmentName}{entry.modulePackName ? ` / ${entry.modulePackName}` : ''}</span>
                      {entry.orderedOn && <span className="ordered-note">Bestellt am {formatOrderDate(entry.orderedOn)}</span>}
                    </Link>
                    <div className="entry-actions">
                      <button
                        className="btn-order-calendar"
                        onClick={() => handleQuickOrder(entry)}
                        disabled={orderingKey === [entry.expiryDate, entry.itemId, entry.materialName, entry.compartmentName, entry.modulePackName || ''].join('|')}
                      >
                        {orderingKey === [entry.expiryDate, entry.itemId, entry.materialName, entry.compartmentName, entry.modulePackName || ''].join('|') ? '...' : 'Bestellen'}
                      </button>
                      <span className={`days-left ${entry.daysLeft < 0 ? 'expired' : entry.daysLeft <= 30 ? 'soon' : ''}`}>
                        {entry.daysLeft < 0 ? `${Math.abs(entry.daysLeft)} Tage überfällig` : `${entry.daysLeft} Tage`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
